import { Badge, Text } from '@mantine/core';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { difficultyColor, dimValue } from '@/shared/surfaces/InteractionRecordDrawer';
import { formatTimestamp, humanUser } from '@/shared/analytics/MetricFormatting';

/** The behavioral cut of the activity feed: model mix, the kind and difficulty of work, and conversation depth. */
export const BEHAVIOR_ACTIVITY_COLUMNS: MegaGridColumn<InteractionRecord>[] = [
    {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.timestamp),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatTimestamp(r.timestamp)}
            </Text>
        ),
    },
    {
        key: 'user',
        header: 'User',
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => dimValue(r, 'user_department'),
        groupValue: (r) => dimValue(r, 'user_department'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'user_department') },
        render: (r) => (
            <Text size="xs" c="dimmed">
                {dimValue(r, 'user_department')}
            </Text>
        ),
    },
    {
        key: 'model',
        header: 'Model',
        sortValue: (r) => r.model,
        filter: { type: 'enum', accessor: (r) => r.model },
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.model}
            </Text>
        ),
    },
    {
        key: 'category',
        header: 'Category',
        sortValue: (r) => dimValue(r, 'task_category'),
        groupValue: (r) => dimValue(r, 'task_category'),
        render: (r) => <Text size="xs">{dimValue(r, 'task_category')}</Text>,
    },
    {
        key: 'task',
        header: 'Task',
        width: 220,
        render: (r) => (
            <Text size="xs" c="dimmed" truncate>
                {dimValue(r, 'task_subcategory')}
            </Text>
        ),
    },
    {
        key: 'difficulty',
        header: 'Difficulty',
        sortValue: (r) => dimValue(r, 'difficulty'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'difficulty') },
        render: (r) => (
            <Badge size="xs" radius="sm" variant="light" color={difficultyColor(dimValue(r, 'difficulty'))}>
                {dimValue(r, 'difficulty')}
            </Badge>
        ),
    },
    {
        key: 'session',
        header: 'Session',
        width: 200,
        groupValue: (r) => r.topicLabel ?? r.sessionTopic ?? '—',
        render: (r) => (
            <Text size="xs" c="dimmed" truncate>
                {r.topicLabel ?? r.sessionTopic ?? '—'}
            </Text>
        ),
    },
    {
        key: 'turn',
        header: 'Prompt #',
        align: 'right',
        sortValue: (r) => r.turnIndex,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {r.turnIndex}
            </Text>
        ),
    },
];
